import React from 'react';
import Panel from '@atlaskit/panel';
import defaultTo from 'lodash/defaultTo';
import { EntitlementIcon } from '@css-xp/base';
import { Currency } from '@css-xp/ppc-common';
import { Estimate } from '@css-xp/ppc-purchase-experience';
import { QuoteOrderSummaryLineItem } from './quote-types';
import './quote-integrated-entity.scss';

export type QuoteIntegratedEntityProps = {
  lineItems: QuoteOrderSummaryLineItem[];
  lineItemId: string;
  totalPriceOfEntity?: number;
  loading: boolean;
  isAddOn?: boolean;
  isAddOnChangesEnabled?: boolean;
  currency?: string;
};

export const QuoteIntegratedEntity: React.FC<QuoteIntegratedEntityProps> = ({
  lineItems,
  lineItemId,
  totalPriceOfEntity,
  loading,
  isAddOn = false,
  isAddOnChangesEnabled,
  currency = Currency.USD,
}) => {
  return (
    <div className="quote-integrated-entity-container">
      <Panel
        header={
          <div className="integrated-apps-container-header-container" key={`integrated-apps-${lineItemId}`}>
            <div className="integrated-apps-container-header-container-header">
              <div className="integrated-apps-container-header-container-header-name-container">
                <h4 className="integrated-apps-container-header-container-header-name">
                  {isAddOn ? `Add-ons` : `Integrated Apps`}
                </h4>
                <Estimate
                  currency={currency}
                  price={defaultTo(totalPriceOfEntity, 0)}
                  loading={loading}
                  className={
                    'integrated-apps-container-line-items-container-line-item-app-container-selected-app-estimated-price'
                  }
                  data-testid="quote-app-lineitem-estimate"
                />
              </div>
            </div>
          </div>
        }
      >
        <div className="integrated-apps-container-line-items-container">
          {lineItems.map((lineItem: QuoteOrderSummaryLineItem) => {
            return (
              <div
                className="integrated-apps-container-line-items-container-line-item-app-container"
                key={`integrated-apps-${lineItem?.lineItemId}`}
              >
                <div className="integrated-apps-container-line-items-container-line-item-app-container-icon">
                  <EntitlementIcon
                    logoId={lineItem?.logoId}
                    productId={lineItem?.productKey}
                    className="avatar medium pt-4"
                    isCloud
                  />
                </div>
                <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app">
                  <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                    <h4 className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-name integrated-app">
                      {lineItem?.name}
                    </h4>
                    <Estimate
                      currency={currency}
                      price={lineItem?.headerTotal ?? 0}
                      loading={loading}
                      className={
                        'integrated-apps-container-line-items-container-line-item-app-container-selected-app-estimated-price'
                      }
                      data-testid={`quote-lineitem-header-estimate-${lineItem.lineItemId}`}
                    />
                  </div>

                  {/* Entitlement Number */}
                  <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                    <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                      Entitlement number
                    </p>
                    <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                      {lineItem?.entitlementNumber || '-'}
                    </p>
                  </div>

                  {/* Plan */}
                  <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                    <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                      {`Plan: ${lineItem?.offeringName}`}
                      {!lineItem?.isFreeProduct ? ', ' + lineItem?.billingCycleLabel : ''}
                    </p>
                  </div>

                  {/* Start Date */}
                  {lineItem?.startDate !== undefined && (
                    <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                      <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                        Start date
                      </p>
                      <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                        {lineItem.startDate}
                      </p>
                    </div>
                  )}

                  {/* Renewal/End Date */}
                  {lineItem?.renewalOrEndDate !== undefined && (
                    <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                      <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                        Renewal / End date
                      </p>
                      <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                        {lineItem.renewalOrEndDate}
                      </p>
                    </div>
                  )}

                  {/* User tier */}
                  {!lineItem.isFreeProduct && (
                    <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                      <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                        {isAddOnChangesEnabled ? `Quantity (${lineItem?.chargeElement})` : `User tier`}
                      </p>
                      <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                        {lineItem?.users}
                      </p>
                    </div>
                  )}

                  {/* List Price */}
                  <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                    <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                      List price{' '}
                    </p>
                    <Estimate
                      currency={currency}
                      price={lineItem?.listPrice ?? 0}
                      loading={loading}
                      className={
                        'integrated-apps-container-line-items-container-line-item-app-container-selected-app-sub-title-pricing-currency'
                      }
                      data-testid={`quote-lineitem-list-price-${lineItem.lineItemId}`}
                    />
                  </div>

                  {/* Other Discounts */}
                  <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                    <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                      Other discounts
                    </p>
                    <Estimate
                      currency={currency}
                      price={lineItem?.discountTotal ? -lineItem.discountTotal : 0}
                      loading={loading}
                      className={
                        'integrated-apps-container-line-items-container-line-item-app-container-selected-app-sub-title-pricing-currency'
                      }
                    />
                  </div>

                  {/* Credits */}
                  <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                    <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                      Credits
                    </p>
                    <Estimate
                      currency={currency}
                      price={lineItem.credits ? -lineItem.credits : 0}
                      loading={loading}
                      className={
                        'integrated-apps-container-line-items-container-line-item-app-container-selected-app-sub-title-pricing-currency'
                      }
                    />
                  </div>

                  {/* Partner Discount */}
                  <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                    <p
                      className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details"
                      data-testid="quote-partner-margin"
                    >
                      Partner discount: {!lineItem.shouldHidePartnerMarginPercentage && `${lineItem?.marginPercent}%`}
                    </p>
                    <Estimate
                      currency={currency}
                      price={lineItem.marginTotal ? -lineItem.marginTotal : 0}
                      loading={loading}
                      className={
                        'integrated-apps-container-line-items-container-line-item-app-container-selected-app-sub-title-pricing-currency'
                      }
                    />
                  </div>

                  {/* Estimate incl. partner discount */}
                  <div
                    className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container"
                    data-testid="quote-lineitem-estimate-with-margin-applied"
                  >
                    <h4 className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">
                      Estimate incl. partner discount
                    </h4>
                    <Estimate
                      currency={currency}
                      price={
                        defaultTo(lineItem.listPrice, 0) -
                        defaultTo(lineItem.discountTotal, 0) -
                        defaultTo(lineItem.credits, 0) -
                        defaultTo(lineItem.marginTotal, 0)
                      }
                      loading={loading}
                      className={
                        'integrated-apps-container-line-items-container-line-item-app-container-selected-app-sub-title-pricing-currency'
                      }
                    />
                  </div>

                  {/* Admin Fee */}
                  {lineItem?.adminFee ? (
                    <div className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container">
                      <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">{`Administration fee (DST)`}</p>
                      <Estimate
                        currency={currency}
                        price={defaultTo(lineItem?.adminFee, 0)}
                        loading={loading}
                        className={
                          'integrated-apps-container-line-items-container-line-item-app-container-selected-app-sub-title-pricing-currency'
                        }
                      />
                    </div>
                  ) : null}

                  {/* Tax */}
                  {lineItem?.itemLevelTaxInfo?.map(
                    ({
                      taxLabel,
                      taxInformation,
                    }: {
                      taxLabel: string;
                      taxInformation: { tax: number | string; taxPercent: number | string };
                    }) => {
                      return (
                        <div
                          key={`tax-(${taxInformation?.taxPercent}-${taxLabel})`}
                          className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-row-container"
                        >
                          <p className="integrated-apps-container-line-items-container-line-item-app-container-selected-app-pricing-details">{`Tax (${taxInformation?.taxPercent}% ${taxLabel})`}</p>
                          <Estimate
                            currency={currency}
                            price={taxInformation.tax as number}
                            loading={loading}
                            className={
                              'integrated-apps-container-line-items-container-line-item-app-container-selected-app-sub-title-pricing-currency'
                            }
                          />
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
};
